/**
 * Quiz themes: `rounds` in theme data (see backend/themes/themesets.js).
 * Rounds 1 & 6: promptType "text" + questionText.
 * Rounds 2–5: promptType "image" + questionImage (filename on S3).
 * All options are plain strings; correctIndex is 0-based into options.
 *
 * Each round: 1–10 questions (admin). Text prompt max 350 chars; each option max 50 chars.
 */

export const MIN_QUIZ_QUESTIONS_PER_ROUND = 1;
export const MAX_QUIZ_QUESTIONS_PER_ROUND = 10;
export const QUIZ_QUESTION_TEXT_MAX_LEN = 150;
export const QUIZ_OPTION_MAX_LEN = 50;

const DEFAULT_ROUND_TITLES = [
  'Iconic Dialogues (MCQ)',
  'Guess The Frame (MCQ)',
  'Bollywood Posters (MCQ)',
  'Music & Playback (MCQ)',
  'Stars & Directors (MCQ)',
  'Grand Finale (MCQ)',
];

export function getMaxQuestionsForRound(_roundIndex0) {
  return MAX_QUIZ_QUESTIONS_PER_ROUND;
}

export function getMinQuestionsForRound(_roundIndex0) {
  return MIN_QUIZ_QUESTIONS_PER_ROUND;
}

/** Parse `rounds` whether the API returned an object, JSON string, or missing. */
export function parseRoundsFromThemeData(data) {
  if (!data || data.rounds == null) return [];
  let r = data.rounds;
  if (typeof r === 'string') {
    try {
      r = JSON.parse(r);
    } catch {
      return [];
    }
  }
  return Array.isArray(r) ? r : [];
}

function emptyQuestion(roundIndex0) {
  const imageRound = roundIndex0 >= 1 && roundIndex0 <= 4;
  return {
    promptType: imageRound ? 'image' : 'text',
    questionText: '',
    questionImage: '',
    options: ['', '', '', ''],
    correctIndex: 0,
  };
}

/** Empty template: 1 placeholder question per round (admin can add up to 10 each). */
export function createDefaultQuizRounds() {
  const rounds = [];
  for (let ri = 0; ri < 5; ri += 1) {
    const sub = DEFAULT_ROUND_TITLES[ri] || `Round ${ri + 1}`;
    const label = `ROUND ${ri + 1}`;
    rounds.push({
      roundLabel: label,
      roundSubtitle: sub,
      roundTitle: `${label}: ${sub}`,
      questions: [emptyQuestion(ri)],
    });
  }
  const sub6 = DEFAULT_ROUND_TITLES[5] || "Round 6";
  const label6 = "ROUND 6";
  rounds.push({
    roundLabel: label6,
    roundSubtitle: sub6,
    roundTitle: `${label6}: ${sub6}`,
    questions: [emptyQuestion(5)],
  });
  return rounds;
}

export function normalizeRounds(raw) {
  if (!raw) return [];
  let r = raw;
  if (typeof r === 'string') {
    try {
      r = JSON.parse(r);
    } catch {
      return [];
    }
  }
  return Array.isArray(r) ? r : [];
}

export function countQuestionsInRounds(rawRounds) {
  const rounds = normalizeRounds(rawRounds);
  return rounds.reduce((sum, round) => {
    const qs = round?.questions;
    return sum + (Array.isArray(qs) ? qs.length : 0);
  }, 0);
}

/** Line 1: ROUND n — Line 2: MCQ title. Supports legacy `roundTitle` ("ROUND 1: Foo"). */
export function deriveRoundDisplay(round, roundIndex0) {
  let roundLabel = String(round?.roundLabel ?? "").trim();
  let roundSubtitle = String(round?.roundSubtitle ?? "").trim();
  const legacyTitle = String(round?.roundTitle ?? "").trim();

  if (!roundLabel) {
    roundLabel = `ROUND ${roundIndex0 + 1}`;
  }
  if (!roundSubtitle && legacyTitle) {
    const colon = legacyTitle.indexOf(":");
    if (colon !== -1) {
      const left = legacyTitle.slice(0, colon).trim();
      const right = legacyTitle.slice(colon + 1).trim();
      if (/^ROUND\s*\d+/i.test(left)) {
        roundLabel = left;
        roundSubtitle = right;
      } else {
        roundSubtitle = legacyTitle;
      }
    } else if (/^ROUND\s*\d+$/i.test(legacyTitle)) {
      roundLabel = legacyTitle;
    } else {
      roundSubtitle = legacyTitle;
    }
  }

  const combined =
    roundSubtitle && roundLabel
      ? `${roundLabel}: ${roundSubtitle}`
      : roundLabel || roundSubtitle || `Round ${roundIndex0 + 1}`;

  return { roundLabel, roundSubtitle, roundTitle: combined };
}

/** When loading admin editor: fill roundLabel / roundSubtitle from legacy roundTitle. */
export function migrateQuizRoundsForAdmin(rawRounds) {
  const rounds = normalizeRounds(rawRounds);
  return rounds.map((round, ri) => {
    const { roundLabel, roundSubtitle, roundTitle } = deriveRoundDisplay(round, ri);
    return { ...round, roundLabel, roundSubtitle, roundTitle };
  });
}

/**
 * Flatten rounds in stable order (round index, then question index).
 * Each slide includes roundLabel, roundSubtitle, roundTitle (combined).
 */
export function flattenRoundsToQuestions(rawRounds, maxQuestions) {
  const rounds = normalizeRounds(rawRounds);
  const out = [];
  const cap = typeof maxQuestions === 'number' && maxQuestions > 0 ? maxQuestions : Infinity;

  for (let ri = 0; ri < rounds.length && out.length < cap; ri += 1) {
    const round = rounds[ri] || {};
    const { roundLabel, roundSubtitle, roundTitle } = deriveRoundDisplay(round, ri);
    const questions = Array.isArray(round.questions) ? round.questions : [];
    for (let qi = 0; qi < questions.length && out.length < cap; qi += 1) {
      const q = questions[qi] || {};
      const promptType = q.promptType === 'image' ? 'image' : 'text';
      const options = Array.isArray(q.options)
        ? q.options.map((o) => String(o ?? '')).filter((s) => s.length > 0)
        : [];
      if (options.length < 2) continue;

      let correctIndex = Number(q.correctIndex);
      if (!Number.isInteger(correctIndex) || correctIndex < 0 || correctIndex >= options.length) {
        correctIndex = 0;
      }

      out.push({
        roundIndex: ri,
        roundLabel,
        roundSubtitle,
        roundTitle,
        questionInRound: qi + 1,
        questionsInRound: questions.length,
        promptType,
        questionText: q.questionText != null ? String(q.questionText) : '',
        questionImage: q.questionImage != null ? String(q.questionImage) : '',
        options,
        correctIndex,
      });
    }
  }
  return out;
}

export function themeUsesQuizRounds(themeData) {
  return countQuestionsInRounds(themeData?.rounds) > 0;
}

/** Returns error message string or null if OK. Enforces 6 rounds, 1–10 questions per round. */
export function validateQuizRoundsForSave(roundsInput) {
  const r = Array.isArray(roundsInput) ? roundsInput : [];
  if (r.length !== 6) {
    return "Exactly 6 rounds are required (use “Load default layout” if starting fresh).";
  }
  for (let ri = 0; ri < 6; ri += 1) {
    const maxQ = getMaxQuestionsForRound(ri);
    const minQ = getMinQuestionsForRound(ri);
    const qs = r[ri]?.questions;
    const n = Array.isArray(qs) ? qs.length : 0;
    if (!Array.isArray(qs) || n < minQ || n > maxQ) {
      return `Round ${ri + 1} must have between ${minQ} and ${maxQ} questions (currently ${n}).`;
    }
    for (let qi = 0; qi < qs.length; qi += 1) {
      const q = qs[qi] || {};
      const promptType = q.promptType === "image" ? "image" : "text";
      if (promptType === "text") {
        const qt = String(q.questionText || "");
        if (!qt.trim()) {
          return `Round ${ri + 1}, question ${qi + 1}: question text is required.`;
        }
        if (qt.length > QUIZ_QUESTION_TEXT_MAX_LEN) {
          return `Round ${ri + 1}, question ${qi + 1}: question text must be at most ${QUIZ_QUESTION_TEXT_MAX_LEN} characters.`;
        }
      }
      if (promptType === "image" && !String(q.questionImage || "").trim()) {
        return `Round ${ri + 1}, question ${qi + 1}: question image filename is required.`;
      }
      const rawOpts = Array.isArray(q.options) ? q.options : [];
      const opts = rawOpts.slice(0, 4).map((o) => String(o ?? "").trim());
      while (opts.length < 4) opts.push("");
      if (opts.some((o) => !o)) {
        return `Round ${ri + 1}, question ${qi + 1}: all four answer options (A–D) must be filled in.`;
      }
      if (opts.some((o) => o.length > QUIZ_OPTION_MAX_LEN)) {
        return `Round ${ri + 1}, question ${qi + 1}: each answer option must be at most ${QUIZ_OPTION_MAX_LEN} characters.`;
      }
      let ci = parseInt(q.correctIndex, 10);
      if (!Number.isFinite(ci)) ci = 0;
      if (ci < 0 || ci > 3) {
        return `Round ${ri + 1}, question ${qi + 1}: pick correct answer A–D.`;
      }
    }
  }
  return null;
}

/** Normalize rounds for API / DB (trim strings, clamp correctIndex). */
export function sanitizeQuizRoundsForSave(roundsInput) {
  const rounds = Array.isArray(roundsInput) ? roundsInput : [];
  return rounds.map((round, ri) => {
    const label =
      String(round?.roundLabel ?? "").trim() || `ROUND ${ri + 1}`;
    const subtitle = String(round?.roundSubtitle ?? "").trim();
    const combined =
      subtitle && label ? `${label}: ${subtitle}` : label || subtitle || `Round ${ri + 1}`;
    return {
    roundLabel: label,
    roundSubtitle: subtitle,
    roundTitle: combined,
    questions: (Array.isArray(round?.questions) ? round.questions : []).map((q) => {
      const promptType = q?.promptType === "image" ? "image" : "text";
      const opts = (Array.isArray(q?.options) ? q.options : [])
        .slice(0, 4)
        .map((o) => String(o ?? "").trim().slice(0, QUIZ_OPTION_MAX_LEN));
      const four = [...opts, "", "", "", ""].slice(0, 4);
      let ci = parseInt(q?.correctIndex, 10);
      if (!Number.isFinite(ci)) ci = 0;
      ci = Math.max(0, Math.min(ci, 3));
      const qText = String(q?.questionText ?? "").slice(0, QUIZ_QUESTION_TEXT_MAX_LEN);
      return {
        promptType,
        questionText: qText,
        questionImage: String(q?.questionImage ?? ""),
        options: four,
        correctIndex: ci,
      };
    }),
  };
  });
}
